import React from 'react';
import { graphql, Link } from 'gatsby';
import localize from '../components/localize';
import {
  BlogContainer,
  BlogPostArticle,
  BlogPostBody,
  BlogPostHeader,
  BlogPostHeroImage,
  BlogPostMeta,
  RelatedPosts,
} from '../styles/BlogStyles';
import SEO from "../components/SEO";

function getBlogPath(locale, slug) {
  const prefix = locale === 'en' ? '/en' : '';
  return `${prefix}/blog/${slug}`;
}

function getBlogIndexPath(locale) {
  return locale === 'en' ? '/en/blog' : '/blog';
}

function formatDate(date, locale) {
  if (!date) return null;

  return new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'pl-PL', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));
}

function getLocalizedValue(value, locale) {
  if (!value) return '';

  if (locale === 'en') {
    return value.en || value.pl || '';
  }

  return value.pl || value.en || '';
}

function getLocalizedBlocks(value, locale) {
  if (!value) return [];

  if (locale === 'en') {
    return value._rawEn || value._rawPl || [];
  }

  return value._rawPl || value._rawEn || [];
}

function renderSpan(child) {
  if (!child || !child.text) return null;

  let content = child.text;

  if (child.marks && child.marks.includes('strong')) {
    content = <strong key={`${child._key}-strong`}>{content}</strong>;
  }

  if (child.marks && child.marks.includes('em')) {
    content = <em key={`${child._key}-em`}>{content}</em>;
  }

  if (child.marks && child.marks.includes('underline')) {
    content = <u key={`${child._key}-underline`}>{content}</u>;
  }

  if (child.marks && child.marks.includes('code')) {
    content = <code key={`${child._key}-code`}>{content}</code>;
  }

  return React.isValidElement(content) ? (
    React.cloneElement(content, { key: child._key })
  ) : (
    <React.Fragment key={child._key}>{content}</React.Fragment>
  );
}

function renderChildren(block) {
  if (!block || !block.children) return null;

  return block.children.map(child => renderSpan(child));
}

function renderNormalBlock(block) {
  const children = renderChildren(block);

  switch (block.style) {
    case 'h1':
      return <h1 key={block._key}>{children}</h1>;
    case 'h2':
      return <h2 key={block._key}>{children}</h2>;
    case 'h3':
      return <h3 key={block._key}>{children}</h3>;
    case 'h4':
      return <h4 key={block._key}>{children}</h4>;
    case 'blockquote':
      return <blockquote key={block._key}>{children}</blockquote>;
    case 'normal':
    default:
      return <p key={block._key}>{children}</p>;
  }
}

function renderPortableText(blocks) {
  if (!blocks || !blocks.length) return null;

  const elements = [];
  let listItems = [];
  let currentListType = null;

  function flushList() {
    if (!listItems.length) return;

    const ListTag = currentListType === 'number' ? 'ol' : 'ul';

    elements.push(
      <ListTag key={`list-${elements.length}`}>
        {listItems}
      </ListTag>
    );

    listItems = [];
    currentListType = null;
  }

  blocks.forEach(block => {
    if (!block) return;

    if (block.listItem) {
      const listType = block.listItem === 'number' ? 'number' : 'bullet';

      if (currentListType && currentListType !== listType) {
        flushList();
      }

      currentListType = listType;

      listItems.push(
        <li key={block._key}>
          {renderChildren(block)}
        </li>
      );

      return;
    }

    flushList();
    elements.push(renderNormalBlock(block));
  });

  flushList();

  return elements;
}

function BlogPostTemplate({ data, pathContext }) {
  const locale = pathContext && pathContext.locale ? pathContext.locale : 'pl';
  const post = data.sanityPost;

  if (!post) {
    return (
      <>
      <SEO title={locale === 'en' ? 'Post not found' : 'Nie znaleziono wpisu'}
      />
        <BlogContainer>
          <BlogPostArticle>
            <Link className="back-link" to={getBlogIndexPath(locale)}>
              {locale === 'en' ? 'Back to blog' : 'Wróć do bloga'}
            </Link>

            <BlogPostHeader>
              <h1>{locale === 'en' ? 'Post not found' : 'Nie znaleziono wpisu'}</h1>
            </BlogPostHeader>
          </BlogPostArticle>
        </BlogContainer>
      </>
    );
  }

  const title = getLocalizedValue(post.title, locale);
  const excerpt = getLocalizedValue(post.excerpt, locale);
  const body = getLocalizedBlocks(post.body, locale);

  const mainImageSrc =
    post.mainImage &&
    post.mainImage.asset &&
    post.mainImage.asset.fluid &&
    post.mainImage.asset.fluid.src;

  const mainImageSrcSet =
    post.mainImage &&
    post.mainImage.asset &&
    post.mainImage.asset.fluid &&
    post.mainImage.asset.fluid.srcSet;

  const mainImageSizes =
    post.mainImage &&
    post.mainImage.asset &&
    post.mainImage.asset.fluid &&
    post.mainImage.asset.fluid.sizes;

  return (
    <>
    <SEO
      title={title || 'Blog post'}
      description={excerpt ? excerpt : locale === 'en' ? 'Bizami blog post page' : 'Strona bloga Bizami'}
    />
      <BlogContainer>
        <BlogPostArticle>
          <Link className="back-link" to={getBlogIndexPath(locale)}>
            {locale === 'en' ? 'Back to blog' : 'Wróć do bloga'}
          </Link>

          <BlogPostHeader>
            {post.publishedAt && (
              <BlogPostMeta>{formatDate(post.publishedAt, locale)}</BlogPostMeta>
            )}

            <h1>{title || 'Blog post'}</h1>

            {excerpt && <p>{excerpt}</p>}
          </BlogPostHeader>

          {mainImageSrc && (
            <BlogPostHeroImage
              src={mainImageSrc}
              srcSet={mainImageSrcSet}
              sizes={mainImageSizes}
              alt={title || 'Blog post'}
            />
          )}

          {body.length > 0 && (
            <BlogPostBody>
              {renderPortableText(body)}
            </BlogPostBody>
          )}

          {post.relatedPosts && post.relatedPosts.length > 0 && (
            <RelatedPosts>
              <h2>{locale === 'en' ? 'Related posts' : 'Powiązane posty'}</h2>

              <ul>
                {post.relatedPosts.map(relatedPost => {
                  const slug = relatedPost.slug && relatedPost.slug.current;
                  const relatedTitle = getLocalizedValue(relatedPost.title, locale);

                  if (!slug) return null;

                  return (
                    <li key={relatedPost.id}>
                      <Link to={getBlogPath(locale, slug)}>
                        {relatedTitle || 'Blog post'}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </RelatedPosts>
          )}
        </BlogPostArticle>
      </BlogContainer>
    </>
  );
}

export default localize(BlogPostTemplate);

export const query = graphql`
  query BlogPostTemplateQuery($id: String!) {
    sanityPost(id: { eq: $id }) {
      id
      title {
        pl
        en
      }
      excerpt {
        pl
        en
      }
      publishedAt
      slug {
        current
      }
      mainImage {
        asset {
          fluid(maxWidth: 1440) {
            src
            srcSet
            sizes
          }
        }
      }
      body {
        _rawPl
        _rawEn
      }
      relatedPosts {
        id
        title {
          pl
          en
        }
        slug {
          current
        }
      }
    }
  }
`;